/**
 * Career DNA™ — the seven-strand framework, with build status.
 *
 * This is the single source of truth for how the framework is *presented*.
 * It is a projection, not a scoring object: nothing here computes anything.
 *
 * Governance (Option A, as ruled):
 *  - The sealed registry (`career_dna_strands`, registry v1.0.0, engine v3.4.0)
 *    holds 4 active strands + 1 reserved: DNA-PUR (Direction), DNA-REL
 *    (Connection), DNA-LRN (Growth Strand), DNA-CAP (Contribution Strand) and
 *    DNA-GRO (Trajectory, primary_intelligence_id NULL).
 *  - Registry names are NOT renamed. Public labels are mapped onto sealed
 *    strand ids here, so the seal stays immutable and the campaign language
 *    stays consistent.
 *  - Strands whose status is not `live` never carry a value or a score. Ever.
 *  - `Opportunity DNA™` and `Contribution DNA™` (public labels) have no sealed
 *    strand row yet, so `strandId` is null and they are catalogue-only until a
 *    governed migration seals them.
 */

export type StrandStatus = "live" | "reserved" | "in_build";

export interface PublicStrand {
  /** Public, trademarked label used in every asset. */
  label: string;
  /** Sealed registry strand id, or null when not yet sealed. */
  strandId: string | null;
  /** Sealed registry name, when a row exists. */
  sealedName: string | null;
  /** Intelligence code on the student payload, when the strand renders a reading. */
  intelligenceCode: string | null;
  status: StrandStatus;
  /** One line: what this strand reads. */
  meaning: string;
  /** Shown instead of a value for non-live strands. */
  statusSentence: string;
}

export const STRAND_STATUS_LABEL: Record<StrandStatus, string> = {
  live: "live",
  reserved: "reserved",
  in_build: "in build",
};

export const CAREER_DNA_STRANDS: PublicStrand[] = [
  {
    label: "Purpose DNA™",
    strandId: "DNA-PUR",
    sealedName: "Direction Strand",
    intelligenceCode: "purpose",
    status: "live",
    meaning: "What you're for — the direction your values and motivation point in.",
    statusSentence: "Reading from your assessment.",
  },
  {
    label: "Strength DNA™",
    strandId: "DNA-REL",
    sealedName: "Connection Strand",
    intelligenceCode: "strength",
    status: "live",
    meaning: "The strengths that keep showing up, and how you use them with people.",
    statusSentence: "Reading from your assessment.",
  },
  {
    label: "Learning DNA™",
    strandId: "DNA-LRN",
    sealedName: "Growth Strand",
    intelligenceCode: "learning",
    status: "live",
    meaning: "How you learn — the loop you complete when something is new and hard.",
    statusSentence: "Reading from your assessment.",
  },
  {
    label: "Capability DNA™",
    strandId: "DNA-CAP",
    sealedName: "Contribution Strand",
    intelligenceCode: "capability",
    status: "live",
    meaning: "What you can already carry, and how steadily you carry it.",
    statusSentence: "Reading from your assessment.",
  },
  {
    label: "Growth DNA™",
    strandId: "DNA-GRO",
    sealedName: "Trajectory Strand",
    intelligenceCode: null,
    status: "reserved",
    meaning: "How far you've moved — trajectory, measured between assessments.",
    statusSentence: "Reserved — trajectory activates with your second assessment.",
  },
  {
    label: "Opportunity DNA™",
    strandId: null,
    sealedName: null,
    intelligenceCode: null,
    status: "in_build",
    meaning: "The openings around you — where your profile meets a real market.",
    statusSentence: "In build — we'll tell you the day it goes live.",
  },
  {
    label: "Contribution DNA™",
    strandId: null,
    sealedName: null,
    intelligenceCode: null,
    status: "in_build",
    meaning: "What you give back — the mark your work leaves on other people.",
    statusSentence: "In build — we'll tell you the day it goes live.",
  },
];

export const CAREER_DNA_FRAMEWORK_INTRO =
  "Career DNA™ is a seven-strand framework. Four strands read from your assessment today. One activates the second time you take it. Two are in build — and we'd rather show you the architecture than pretend it's finished.";

export function isLive(strand: PublicStrand): boolean {
  return strand.status === "live";
}

export function strandCounts(): Record<StrandStatus, number> {
  return CAREER_DNA_STRANDS.reduce(
    (acc, s) => ({ ...acc, [s.status]: acc[s.status] + 1 }),
    { live: 0, reserved: 0, in_build: 0 } as Record<StrandStatus, number>,
  );
}
